import type { StorefrontAdapter, StorefrontCartItem, StorefrontOrder, StorefrontProduct } from '../types';
import { loadScriptOnce } from '../types';
import { trackingConsentGranted } from '../consent';

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue?: unknown[]; loaded?: boolean; version?: string; push?: unknown };
    _fbq?: unknown;
  }
}

function money(n: unknown): number {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? Math.round(v * 100) / 100 : 0;
}

/** Meta's standard pre-load stub: calls queue until fbevents.js arrives. */
function ensureFbq(): (...args: unknown[]) => void {
  if (!window.fbq) {
    const n: any = function fbq(...args: unknown[]) {
      if (n.callMethod) n.callMethod(...args);
      else n.queue.push(args);
    };
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    window.fbq = n;
    if (!window._fbq) window._fbq = n;
  }
  return window.fbq!;
}

function contents(items: StorefrontCartItem[]) {
  return items.map((i) => ({ id: i.sku ?? i.variant_id ?? i.product_id, quantity: i.quantity, item_price: money(i.price) }));
}

let liveConfig: Record<string, string> | null = null;
let pixelInitialised = false;

function allowed(): boolean {
  return !!liveConfig && (liveConfig.require_consent !== '1' || trackingConsentGranted());
}

function purchaseAlreadySent(orderId: string): boolean {
  try {
    const key = `rumio_meta_purchase_${orderId}`;
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, '1');
  } catch {
    /* storage blocked: fire anyway */
  }
  return false;
}

export const metaAdapter: StorefrontAdapter = {
  id: 'meta',
  isConfigured: (cfg) => typeof cfg.pixel_id === 'string' && /^\d{5,25}$/.test(cfg.pixel_id),
  load(cfg) {
    liveConfig = cfg;
    if (!allowed()) return;
    const fbq = ensureFbq();
    if (!pixelInitialised) {
      fbq('init', cfg.pixel_id);
      fbq('track', 'PageView');
      pixelInitialised = true;
    }
    loadScriptOnce('meta-pixel', 'https://connect.facebook.net/en_US/fbevents.js');
  },
  viewedProduct(p: StorefrontProduct) {
    if (!allowed()) return;
    ensureFbq()('track', 'ViewContent', { content_type: 'product', content_ids: [p.sku ?? p.id], content_name: p.name, value: money(p.price), currency: liveConfig!.currency || 'USD' });
  },
  addedToCart(item) {
    if (!allowed()) return;
    ensureFbq()('track', 'AddToCart', { content_type: 'product', content_ids: [item.sku ?? item.variant_id], contents: contents([item]), value: money(item.price * item.quantity), currency: liveConfig!.currency || 'USD' });
  },
  startedCheckout(cart, total) {
    if (!allowed()) return;
    ensureFbq()('track', 'InitiateCheckout', { content_type: 'product', content_ids: cart.map((i) => i.sku ?? i.variant_id), contents: contents(cart), num_items: cart.reduce((s, i) => s + i.quantity, 0), value: money(total), currency: liveConfig!.currency || 'USD' });
  },
  orderCompleted(order: StorefrontOrder) {
    if (!allowed()) return;
    if (purchaseAlreadySent(order.id)) return;
    const items = order.items ?? [];
    // eventID = order id: the server-side Conversions API event uses the same id, so Meta deduplicates the pair.
    ensureFbq()(
      'track',
      'Purchase',
      { content_type: 'product', content_ids: items.map((i) => i.sku ?? i.variant_id), contents: contents(items), num_items: items.reduce((s, i) => s + i.quantity, 0), value: money(order.total), currency: order.currency || liveConfig!.currency || 'USD' },
      { eventID: order.id }
    );
  },
  refresh() {
    if (!allowed() || !pixelInitialised) return;
    ensureFbq()('track', 'PageView');
  },
};

/** Test seam. */
export function _resetMetaAdapterForTests(): void {
  liveConfig = null;
  pixelInitialised = false;
}
